import { sportLabel } from "@/types/sports.meta";
import { isSportType } from "@/utils/sports.utils";
import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useCallback } from "react";

type SeriesToggleLegendProps = {
  series: string[];
  hidden: Record<string, boolean>;
  onToggle: (key: string) => void;
};

export function SeriesToggleLegend({
  series,
  hidden,
  onToggle,
}: SeriesToggleLegendProps) {
  const { i18n } = useLingui();

  if (!series.length) return null;

  const getLabel = useCallback(
    (key: string) => (isSportType(key) ? i18n._(sportLabel(key)) : key),
    [i18n],
  );

  return (
    <div
      className={[
        "p-2",
        "flex flex-nowrap items-center justify-start gap-1.5",
        "overflow-x-auto overscroll-x-contain",
        "whitespace-nowrap",
        "[-webkit-overflow-scrolling:touch]",
        "md:justify-center md:flex-wrap md:overflow-x-visible md:whitespace-normal",
      ].join(" ")}
    >
      {series.map((key) => {
        const isOff = hidden[key] === true;
        const label = getLabel(key);

        return (
          <button
            key={key}
            type="button"
            aria-pressed={!isOff}
            onClick={() => onToggle(key)}
            className={[
              "inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs transition",
              "bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring",
              isOff ? "opacity-40" : "opacity-100",
              "shrink-0",
            ].join(" ")}
            title={isOff ? t`Show ${label}` : t`Hide ${label}`}
          >
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{ background: `var(--color-${key})` }}
            />
            <span className="max-w-[160px] truncate">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
